import React, { useEffect, useMemo, useState } from 'react';
import { Empty } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import Form from 'formik-antd/lib/form';
import { Input } from 'formik-antd/lib/input';
import constants from 'jsx/common/constants';
import SubmitButton from 'jsx/common/SubmitButton';
import {
  Action,
  APIAsset,
  ApiAutomation,
  Condition,
  Divider,
  Flex,
  FormGroup,
  Grid,
  insertObjIf,
  Loading,
  useFetch,
} from '@mss/mss-component-library';
import { Minimize, Play, Plus, Send } from 'react-feather';
import { useTranslation } from 'react-i18next';
import ActionForm, { AddActionButton } from './ActionForm';
import AutomationStep from './AutomationStep';
import ConditionForm, { AddConditionButton } from './ConditionForm';
import TriggerForm from './TriggerForm';
import { JSONSchema7 } from 'json-schema';
import moment from 'moment';
import { useAllAssetsText } from '../util/utils';
import isEmpty from 'lodash/isEmpty';

const cType = constants.automation.types.condition;
const tType = constants.automation.types.trigger;

export const initialCondition = (key: string) => ({
  type: cType.EVENT_RANGE,
  settings: {},
  key,
  conditions: undefined,
});

export const initialAction = (key: string) => ({
  type: 'CALL_SERVICE',
  key,
  settings: {
    service: 'email',
    send_custodian_email: false,
    send_custom_email: false,
  },
});

const emptyAutomation: ApiAutomation = {
  name: '',
  description: '',
  actions: [
    {
      type: 'CALL_SERVICE',
      key: 'A-0',
      settings: {
        service: 'email',
        send_custodian_email: false,
        send_custom_email: false,
      },
    },
  ],
  conditions: [],
  trigger: {
    type: tType.EVENT_RANGE,
    key: 'T1',
    settings: {},
  },
};

type Props = {
  handleSubmit: (
    values: ApiAutomation,
    helpers?: FormikHelpers<any>
  ) => Promise<void>;
  automation?: ApiAutomation;
  reinitialize?: boolean;
  disabled: string;
};

export type ServicenowConfig = {
  id: string;
  name: string;
  json_schema: JSONSchema7;
  json_schema_case: JSONSchema7;
};

export type AutomationAsset = {
  id: string;
  name: string;
};

export const AutomationsContext = React.createContext<{
  assets: AutomationAsset[] | null;
  assetsSelectDataSource: AutomationAsset[] | [];
  assetToSearch: string;
  setAssetToSearch: any;
  entities: string[] | null;
  triggerAttributes: { [key: string]: [] } | null;
  servicenowConfigs: ServicenowConfig[];
}>({
  assets: null,
  assetsSelectDataSource: [],
  assetToSearch: '',
  setAssetToSearch: () => null,
  entities: null,
  servicenowConfigs: [],
  triggerAttributes: {},
});

const secondsToHMS = (seconds: number) => {
  if (seconds === undefined || seconds === 0) {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }
  const hh = Math.floor(seconds / 60 / 60);
  const hseconds = hh * 60 * 60;

  const mm = Math.floor((seconds - hseconds) / 60);
  const mseconds = mm * 60;

  const ss = Math.floor(seconds - hseconds - mseconds);
  return {
    hours: `${`${hh}`.length === 1 ? `0${hh}` : hh}`,
    minutes: `${`${mm}`.length === 1 ? `0${mm}` : mm}`,
    seconds: `${`${ss}`.length === 1 ? `0${ss}` : ss}`,
  };
};

const componentFromDb = (component_source: any) => {
  /*
  Format trigger and condition settings so the retrieved data from the backend
  is formatted to be used in the frontend.

  One main formatter is the way we store/send dates or times.
  Next we also defined different fieldnames. This is hardcoded over here.
  I know this is not nice. But there is no other place for now to do so.
   */
  const settingsKeys = Object.keys(component_source.settings);
  if (settingsKeys.length === 0) return component_source;

  const component = {
    ...component_source,
    settings: { ...component_source.settings, temp: {} },
  };

  if (
    component.type === 'STATE_CHANGE' ||
    component.type === 'STATE_CHANGE_OPERATOR'
  ) {
    // Can be empty on init.
    if (component.settings.time === undefined) {
      component.settings.time = 0;
    }
    component.settings.temp.time = secondsToHMS(component.settings.time);
  } else if (component.type === 'EVENT_RANGE') {
    // Can be empty on init.
    if (component.settings.time_period_seconds === undefined) {
      component.settings.time_period_seconds = 0;
    }
    component.settings.temp.time = secondsToHMS(
      component.settings.time_period_seconds
    );
  } else if (component.type === 'TIME') {
    const tmpMoment = moment(component.settings.time, 'HH:mm:ss');

    const toLocale = moment(tmpMoment).local().format('HH:mm:ss');

    const timeArray = toLocale.split(':');
    const [hh, mm, ss] = [...timeArray];

    component.settings.temp.time = {
      hours: hh,
      minutes: mm,
      seconds: ss,
    };
  } else if (component.type === 'TIME_PATTERN') {
    component.settings.temp.time = {
      hours: component.settings.hours,
      minutes: component.settings.minutes,
      seconds: component.settings.seconds,
    };
  }

  return component;
};

const convertAutomation = (automation?: ApiAutomation): ApiAutomation & any => {
  if (automation == null) return automation;
  const conditionsArr =
    Object.keys(automation.conditions).length > 0
      ? // @ts-ignore
        automation.conditions?.conditions
      : [];
  const conditions = conditionsArr.map((c: any) => {
    let newC = { ...c };
    if (Array.isArray(c.conditions) && c.conditions.length > 0) {
      if (newC.type === 'CONDITIONS')
        newC = { ...newC, type: `${newC.type}_${c.operator}` };
      return {
        ...newC,
        conditions: newC.conditions.map((cc: Condition) => ({
          ...componentFromDb(cc),
          settings: {
            ...componentFromDb(cc).settings,
            ...insertObjIf(cc.settings.asset_id === '', {
              asset_id: useAllAssetsText,
            }),
          },
        })),
      };
    }
    return {
      ...componentFromDb(c),
      settings: {
        ...componentFromDb(c).settings,
        ...insertObjIf(c.settings.asset_id === '', {
          asset_id: useAllAssetsText,
        }),
      },
    };
  });

  const actions = automation.actions.map((a: Action) => ({
    ...a,
    settings: {
      ...a.settings,
      asset_id: !a.settings.asset_id ? useAllAssetsText : a.settings.asset_id,
      custodian: !a.settings.custodian ? 'empty' : a.settings.custodian,
    },
  }));

  const emptyAssetIdAutomation = {
    ...automation,
    trigger: componentFromDb(automation.trigger),
    conditions,
    actions,
  };
  if (emptyAssetIdAutomation.trigger?.settings.asset_id === '') {
    emptyAssetIdAutomation.trigger.settings.asset_id = useAllAssetsText;
  }

  emptyAssetIdAutomation.conditions?.conditions?.forEach((condition: any) => {
    if (condition.settings?.asset_id === '') {
      condition.settings.asset_id = useAllAssetsText;
    }
  });

  return emptyAssetIdAutomation;
};

const AutomationForm = ({
  handleSubmit,
  automation,
  reinitialize,
  disabled,
}: Props) => {
  const [t] = useTranslation(['configuration', 'common']);
  const [assets, setAssets] = useState<{ id: string; name: string }[] | null>(
    null
  );
  const [assetsSelectDataSource, setAssetsSelectDataSource] = useState<
    AutomationAsset[] | []
  >([]);
  const [assetToSearch, setAssetToSearch] = useState<string>('');

  const [entities, setEntities] = useState<string[] | null>(null);
  const [triggerAttributes, setTriggerAttributes] = useState<{
    [key: string]: [];
  } | null>(null);

  const [{ loading }] = useFetch<APIAsset[]>({
    url: '/api/assets/?deleted=false',
    onEmpty: () => setAssets([]),
    onSucces: ({ data }: any) => {
      setAssets(data);
    },
  });

  const [, refetchAssets] = useFetch<APIAsset[]>({
    url: `/api/assets/autocomplete/name/?name=${assetToSearch}&limit=10`,
    onEmpty: () => setAssetsSelectDataSource([]),
    onSucces: ({ data }: any) => {
      if (assetToSearch) {
        setAssetsSelectDataSource(data);
      }
    },
  });

  useEffect(() => {
    refetchAssets();
  }, [assetToSearch]);

  const [{ loading: entitiesLoading }] = useFetch<string[]>({
    url: '/api/automation/entities/',
    onEmpty: () => setEntities([]),
    onSucces: ({ data }: any) => {
      setEntities(data);
    },
  });

  const [{ loading: triggerAttributesLoading }] = useFetch<{}>({
    url: '/api/automation/trigger-attributes/',
    onEmpty: () => setEntities([]),
    onSucces: ({ data }: any) => {
      setTriggerAttributes(data);
    },
  });

  const [{ data: servicenowConfigs, loading: servicenowConfigsLoading }] =
    useFetch<ServicenowConfig[]>({
      url: '/api/servicenowconfig/',
    });

  const initialValues = useMemo(
    () => convertAutomation(automation) || emptyAutomation,
    [automation]
  );

  if (automation)
    if (
      (loading ||
        servicenowConfigsLoading ||
        entitiesLoading ||
        triggerAttributesLoading) &&
      !assets &&
      !entities &&
      !triggerAttributes
    )
      return <Loading />;
  if (
    !loading &&
    !servicenowConfigsLoading &&
    !assets &&
    !entities &&
    !triggerAttributes
  )
    return (
      <Empty
        description={t(
          `No assets or applications found to trigger an automation on.`
        )}
      />
    );

  return (
    <AutomationsContext.Provider
      value={{
        assets,
        assetsSelectDataSource,
        assetToSearch,
        setAssetToSearch,
        entities,
        servicenowConfigs,
        triggerAttributes,
      }}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={reinitialize}
      >
        {({ values, errors, setFieldValue, setFieldTouched }) => {
          const addCondition =
            (index: number, nestedIndex?: number) => (e: any) => {
              e.preventDefault();
              const newConditions = [...values.conditions];

              if (nestedIndex == null)
                newConditions.push(initialCondition(`C${index}`));
              if (nestedIndex != null) {
                newConditions[nestedIndex].conditions = [
                  ...newConditions[nestedIndex].conditions,
                  initialCondition(`C${nestedIndex}-${index}`),
                ];
              }
              setFieldValue(`conditions`, newConditions);
            };

          const removeCondition =
            (index: number, nestedIndex?: number) => (e: any) => {
              e.preventDefault();
              const newConditions = [...values.conditions];

              if (nestedIndex == null) newConditions.splice(index, 1);
              if (nestedIndex != null)
                newConditions[nestedIndex].conditions.splice(index, 1);

              setFieldValue('conditions', newConditions);
              setTimeout(() => {
                setFieldTouched('conditions');
              }, 300);
            };

          const addAction = (index: number) => (e: any) => {
            e.preventDefault();
            const newActions = [
              ...values.actions,
              initialAction(`A-${index + 1}`),
            ];
            setFieldValue(`actions`, newActions);
          };

          const removeAction = (index: number) => (e: any) => {
            e.preventDefault();
            const newActions = values.actions
              .filter((action: object, i: number) => i !== index)
              .map((action: object, i: number) => ({
                ...action,
                key: `A-${i + 1}`,
              }));
            setFieldValue('actions', newActions);
            setTimeout(() => {
              setFieldTouched('actions');
            }, 300);
          };

          return (
            <Form>
              <Grid.Row>
                <Grid.Col width={4}>
                  <FormGroup
                    required
                    label={t('Name')}
                    input={<Input required name="name" disabled={!!disabled} />}
                  />
                </Grid.Col>
                <Grid.Col width={8}>
                  <FormGroup
                    label={t('Description')}
                    input={
                      <Input.TextArea
                        autoSize
                        name="description"
                        disabled={!!disabled}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Divider />
              <Grid.Row className="mt-12">
                <Grid.Col width={12}>
                  <AutomationStep
                    name={t('Triggers')}
                    icon={<Play style={{ marginLeft: 4 }} />}
                    desc={t(
                      'Triggers are what starts the processing of an automation rule. For each automation rule one trigger can be specified.'
                    )}
                  >
                    <TriggerForm trigger={values.trigger} disabled={disabled} />
                  </AutomationStep>
                  <AutomationStep
                    padding={false}
                    name={t('Conditions')}
                    icon={<Minimize />}
                    desc={t(
                      'Conditions are optional and will prevent further execution unless all conditions are satisfied.'
                    )}
                  >
                    {values.conditions.map((c: any, i: number) => {
                      return (
                        <ConditionForm
                          key={c.key}
                          addCondition={addCondition}
                          removeCondition={removeCondition}
                          condition={c}
                          index={i}
                          last={values.conditions.length - 1 === i}
                          disabled={disabled}
                        />
                      );
                    })}
                    {values.conditions.length < 1 && (
                      <AddConditionButton
                        disabled={false}
                        empty
                        onClick={addCondition(0)}
                      >
                        <>
                          <Plus color="#96A9A7" className="mr-1" />
                          {t('Add condition')}
                        </>
                      </AddConditionButton>
                    )}
                  </AutomationStep>
                  <AutomationStep
                    padding={false}
                    trailing={false}
                    name={t('Actions')}
                    icon={<Send style={{ margin: '3px 1px 0 0' }} />}
                    desc={t(
                      'The actions are what MSS will do when the automation is triggered.'
                    )}
                  >
                    {values.actions.map((a: Action, i: number) => {
                      return (
                        <ActionForm
                          index={i}
                          action={a}
                          key={a.key}
                          last={values.actions.length - 1 === i}
                          addAction={addAction}
                          removeAction={removeAction}
                          disabled={disabled}
                        />
                      );
                    })}

                    {values.actions.length < 1 && (
                      <AddActionButton
                        empty
                        onClick={addAction(0)}
                        data-ta="addConditionButton"
                      >
                        <Plus color="#96A9A7" /> {t('configuration:Add action')}
                      </AddActionButton>
                    )}
                  </AutomationStep>
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col>
                  <Flex h="start" style={{ marginLeft: 80 }}>
                    {!isEmpty(errors) ? (
                      <p style={{ color: '#fa4654' }}>
                        {t('Please fill out all required fields')}
                      </p>
                    ) : null}
                  </Flex>
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col>
                  <Flex h="end">
                    <SubmitButton disabled={!!disabled || !isEmpty(errors)}>
                      {t('Submit')}
                    </SubmitButton>
                  </Flex>
                </Grid.Col>
              </Grid.Row>
            </Form>
          );
        }}
      </Formik>
    </AutomationsContext.Provider>
  );
};

export default AutomationForm;
